import { TransformControls as DreiTransformControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useState } from "react";

type ControlsProps = {
    selectedUUID: string | null;
    skipSettingModelData: boolean;
    onModelChange?: (
        uuid: string,
        xPosition: number,
        yPosition: number,
        zPosition: number,
        xRotaton: number,
        yRotaton: number,
        zRotaton: number,
        xScale: number,
        yScale: number,
        zScale: number
    ) => void;
    onPOIChange?: (
        uuid: string,
        data: {
            POIXPosition?: number;
            POIYPosition?: number;
            POIZPosition?: number;
        }
    ) => void;
    onPOILineEndpointChange?: (
        uuid: string,
        data: {
            POILineEndpointXPosition?: number;
            POILineEndpointYPosition?: number;
            POILineEndpointZPosition?: number;
        }
    ) => void;
};

export const TransformControls = ({ selectedUUID, skipSettingModelData, onModelChange, onPOIChange, onPOILineEndpointChange }: ControlsProps) => {
    const scene = useThree(state => state.scene);
    const selected3DObject = selectedUUID ? scene.getObjectByProperty('uuid', selectedUUID) : null;
    const [transformControlsMode, setTransformControlsMode] = useState<'translate' | 'rotate' | 'scale'>('translate');
    const [prevSelectedId, setPrevSelectedId] = useState(selectedUUID);

    if (prevSelectedId !== selectedUUID) {
        setPrevSelectedId(selectedUUID);
        setTransformControlsMode('translate');
    }

    useFrame(() => {
        if (!selected3DObject || selectedUUID === null || skipSettingModelData) return;

        const isPOI = (selected3DObject.userData as any)?.isPOI;
        const isPOILineEndpoint = (selected3DObject.userData as any)?.isPOILineEndpoint;

        if (isPOI) {

            if (onPOIChange) {
                onPOIChange(
                    selectedUUID,
                    {
                        POIXPosition: selected3DObject.position.x,
                        POIYPosition: selected3DObject.position.y,
                        POIZPosition: selected3DObject.position.z
                    }
                );
            }

        } else if (isPOILineEndpoint) {

            if (onPOILineEndpointChange) {
                onPOILineEndpointChange(
                    selectedUUID,
                    {
                        POILineEndpointXPosition: selected3DObject.position.x,
                        POILineEndpointYPosition: selected3DObject.position.y,
                        POILineEndpointZPosition: selected3DObject.position.z
                    }
                );
            }

        } else {

            if (onModelChange) onModelChange(
                    selectedUUID,
                    selected3DObject.position.x,
                    selected3DObject.position.y,
                    selected3DObject.position.z,
                    selected3DObject.rotation.x,
                    selected3DObject.rotation.y,
                    selected3DObject.rotation.z,
                    selected3DObject.scale.x,
                    selected3DObject.scale.y,
                    selected3DObject.scale.z
                );

        }
    });

    useEffect(() => {
        if (selectedUUID && selected3DObject) {
            const isPOI = (selected3DObject.userData as any)?.isPOI;
            const isPOILineEndpoint = (selected3DObject.userData as any)?.isPOILineEndpoint;

            if (!isPOI && !isPOILineEndpoint) {
                const handleKeydown = (event: KeyboardEvent) => {
                    if (event.key?.toLowerCase() === 'r') setTransformControlsMode('rotate');
                    else if (event.key?.toLowerCase() === 't') setTransformControlsMode('translate');
                    else if (event.key?.toLowerCase() === 's') setTransformControlsMode('scale');
                };

                window.addEventListener('keydown', handleKeydown);

                return () => {
                    window.removeEventListener('keydown', handleKeydown);
                };
            }
        }
    }, [selectedUUID, selected3DObject]);

    return (
        <>
            { selected3DObject && <DreiTransformControls object={selected3DObject} mode={transformControlsMode} /> }
        </>
    );
};